import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

const fieldSx = {
  "& .MuiInputBase-root": {
    display: "flex !important",
    placeItems: "unset !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    width: "100% !important",
  },
  "& .MuiInputBase-input": {
    width: "100% !important",
    textAlign: "left !important",
  },
  "& .MuiInputBase-inputMultiline": {
    width: "100% !important",
    textAlign: "left !important",
    display: "block !important",
  },
  "& textarea": {
    width: "100% !important",
    textAlign: "left !important",
  },
};

const numberFieldSx = {
  ...fieldSx,
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

export default function StreakGlobalSettingsDialog({
  open,
  onClose,
  onSave,
  initialData,
  loading,
}) {
  const [form, setForm] = useState({
    grace_days_allowed: "",
    grace_window_reset_days: "",
  });

  // Prefill when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        grace_days_allowed: initialData?.grace_days_allowed || "",
        grace_window_reset_days: initialData?.grace_window_reset_days || "",
      });
    }
  }, [open, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only numbers
    if (value === "" || /^[0-9]+$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = () => {
    onSave({
      grace_days_allowed: Number(form.grace_days_allowed),
      grace_window_reset_days: Number(form.grace_window_reset_days),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Update Streak Settings</DialogTitle>

      <DialogContent>
        <Box mt={1} display="flex" flexDirection="column" gap={2}>
          <Box mt={1} display="flex" flexDirection="column" gap={1}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ minWidth: "160px" }}>
            Grace Days Allowed
          </SoftTypography>
          <TextField
            // label="Grace Days Allowed"
            name="grace_days_allowed"
            value={form.grace_days_allowed}
            // onChange={handleChange}
            fullWidth
            type="number"
            onChange={(e) => {
                const val = e.target.value;

                // allow only digits (0–9)
                if (val === "" || /^[0-9]+$/.test(val)) {
                handleChange({
                    target: {
                        name: "grace_days_allowed",
                        value: val,
                    },
                });
                }
            }}
            onKeyDown={(e) => {
                // block everything except digits
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",     // ✅ allow
                      "ArrowDown",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
            }}
            inputProps={{
                inputMode: "numeric", // mobile numeric keyboard
                pattern: "[0-9]*",
            }}
            sx={numberFieldSx}
          />
          </Box>

          <Box mt={1} display="flex" flexDirection="column" gap={1}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ minWidth: "160px" }}>
            Grace Window Reset Days
          </SoftTypography>
          <TextField
            // label="Grace Window Reset Days"
            name="grace_window_reset_days"
            value={form.grace_window_reset_days}
            // onChange={handleChange}
            fullWidth
            type="number"
            onChange={(e) => {
                const val = e.target.value;

                // allow only digits (0–9)
                if (val === "" || /^[0-9]+$/.test(val)) {
                handleChange({
                    target: {
                        name: "grace_window_reset_days",
                        value: val,
                    },
                });
                }
            }}
            onKeyDown={(e) => {
                // block everything except digits
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",     // ✅ allow
                      "ArrowDown",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
            }}
            inputProps={{
                inputMode: "numeric", // mobile numeric keyboard
                pattern: "[0-9]*",
            }}
            sx={numberFieldSx}
          />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <SoftButton onClick={onClose} color="secondary">
          Cancel
        </SoftButton>

        <SoftButton
          onClick={handleSubmit}
          variant="gradient"
          color="info"
          disabled={
            loading ||
            !form.grace_days_allowed ||
            !form.grace_window_reset_days
          }
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save"
          )}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

StreakGlobalSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,

  initialData: PropTypes.shape({
    grace_days_allowed: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    grace_window_reset_days: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }),
};